import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * LightSelect — a compact, light-themed dropdown that matches the Company View
 * glass aesthetic (frosted translucent menu, luminous border) instead of the
 * dark SoftSelect menu. Menu is portalled to the body so it is never clipped by
 * a card's overflow.
 */
// Mobile's floating BottomNav dock sits fixed 14px off the bottom edge with a
// 66px min-height (plus the safe-area inset) at z-[1000] — well below this
// menu's own z-index, so without a height cap the menu just renders on top of
// it instead of stopping short. Reserved only below the dock's own md:hidden
// breakpoint; desktop has no dock to clear.
const MOBILE_BREAKPOINT_QUERY = "(max-width: 767px)";
const BOTTOM_NAV_RESERVE = 96;
const VIEWPORT_EDGE_MARGIN = 12;

const LightSelect = ({ options = [], value, onChange, width = 210, placeholder = "Select", height = 40, fontSize = 13, radius = 10, iconOnly = false, icon = null, menuWidth = 200 }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [maxMenuHeight, setMaxMenuHeight] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => o.value === value) || null;
  // Icon-only mode: a square trigger showing just `icon` (no label/chevron),
  // used to compact the control on mobile. The portalled menu keeps a
  // comfortable width and right-aligns to the trigger so it stays on screen.
  const controlW = iconOnly ? height : width;

  const toggle = () => {
    if (!open && ref.current) {
      const nextRect = ref.current.getBoundingClientRect();
      setRect(nextRect);
      const isMobile = window.matchMedia?.(MOBILE_BREAKPOINT_QUERY)?.matches;
      const reserve = (isMobile ? BOTTOM_NAV_RESERVE : 0) + VIEWPORT_EDGE_MARGIN;
      setMaxMenuHeight(Math.max(120, window.innerHeight - nextRect.bottom - 6 - reserve));
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // The menu is portalled to <body>, so it isn't a descendant of `ref`.
      // Only close when the click is outside BOTH the control and the menu.
      const inControl = ref.current && ref.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inControl && !inMenu) setOpen(false);
    };
    const onMove = (e) => {
      // Scrolling *inside* the now-scrollable menu also fires this listener
      // (capture-phase "scroll" on window sees it regardless of bubbling) —
      // only treat it as "the page moved out from under the menu" when the
      // scroll didn't originate from the menu itself.
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ width: controlW, position: "relative", fontFamily: "DM Sans" }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={iconOnly ? (selected?.label || placeholder) : undefined}
        style={{
          width: "100%",
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: iconOnly ? "center" : "space-between",
          gap: iconOnly ? 0 : 8,
          padding: iconOnly ? 0 : "0 12px",
          background: "var(--tvd-tile, #ffffff)",
          border: `1px solid ${open ? "var(--gmb-primary, #1B598E)" : "var(--tvd-hair, #EAECF0)"}`,
          borderRadius: radius,
          cursor: "pointer",
          fontFamily: "DM Sans",
          fontSize,
          fontWeight: 500,
          color: open ? "var(--gmb-primary, #1B598E)" : "var(--tvd-ink, #101828)",
          boxShadow: open ? "0 0 0 3px var(--gmb-primary-soft, rgba(27,89,142,0.12))" : "none",
          transition: "border-color 150ms ease, box-shadow 150ms ease, color 150ms ease",
        }}
      >
        {iconOnly ? (
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: "inherit" }}>
            {icon}
          </span>
        ) : (
          <>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected?.label || placeholder}
            </span>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms ease", flexShrink: 0 }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </>
        )}
      </button>

      {open && rect &&
        createPortal(
          <div
            ref={menuRef}
            /* Purely additive: a stable hook for a host popover (also portaled
               to <body>, so a raw DOM "click outside" check can't see this
               menu as one of its own React children) to recognise "still
               inside the picker" and not treat a menu click as an outside
               click. */
            data-lightselect-menu="true"
            style={{
              position: "fixed",
              top: rect.bottom + 6,
              left: iconOnly ? Math.max(8, rect.right - menuWidth) : rect.left,
              width: iconOnly ? menuWidth : rect.width,
              zIndex: 13000,
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(16px) saturate(160%)",
              WebkitBackdropFilter: "blur(16px) saturate(160%)",
              border: "1px solid rgba(255,255,255,0.9)",
              boxShadow: "0 12px 32px rgba(16,24,40,0.16)",
              borderRadius: 12,
              padding: 6,
              fontFamily: "DM Sans",
              maxHeight: maxMenuHeight ?? undefined,
              overflowY: "auto",
            }}
          >
            {options.length === 0 && (
              <div style={{ padding: "9px 10px", fontSize: 13, color: "#98A2B3" }}>No options</div>
            )}
            {options.map((o) => {
              const isSel = o.value === value;
              return (
                <div
                  key={o.value}
                  onClick={() => { onChange(o); setOpen(false); }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "9px 10px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: isSel ? 600 : 500,
                    color: isSel ? "var(--gmb-primary, #1B598E)" : "#344054",
                    background: isSel ? "var(--gmb-primary-soft, rgba(27,89,142,0.08))" : "transparent",
                  }}
                  onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = "rgba(16,24,40,0.05)"; }}
                  onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                  {isSel && (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default LightSelect;
